import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FiLoader, FiTrash2, FiEdit2, FiX } from 'react-icons/fi';
import ConfirmModal from '../../common/ConfirmModal';


interface Question {
  id: string;
  name: string;
  order: string;
  answers: Answer[];
}

interface Answer {
  id: string;
  name: string;
  order: string;
}

interface Category {
  id: number;
  name: string;
  created_at: string;
  questions?: Question[]; // Optionnel car pas toutes les catégories ont des questions
}

interface ListCategoriesProps {
  onEditCategory?: (categoryId: number) => void;
}

const ListCategories: React.FC<ListCategoriesProps> = ({ onEditCategory }) => {
  const [categories, setCategories] = useState<Category[]>([]);
  // const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<Category | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [isAdding, setIsAdding] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');

  const [page, setPage] = useState(1);
  const itemsPerPage = 10;

  const fetchCategories = async () => {
    try {
      // setLoading(true);
      setError(null);
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/categories`);
      setCategories(response.data);
    } catch (error: any) {
      if (error.response) {
        setError(`Erreur ${error.response.status}: ${error.response.data?.error || error.response.statusText}`);
      } else if (error.request) {
        setError('Erreur de connexion au serveur');
      } else {
        setError('Erreur inattendue: ' + error.message);
      }
    } finally {
      // setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    // Clear success and error messages after a timeout
    if (successMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage(null);
      }, 5000); // Hide after 5 seconds
      return () => clearTimeout(timer);
    }
    if (errorMessage) {
      const timer = setTimeout(() => {
        setErrorMessage(null);
      }, 5000); // Hide after 5 seconds
      return () => clearTimeout(timer);
    }
  }, [successMessage, errorMessage]);

  const filteredCategories = (categories || []).filter(category =>
    category.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const sortedCategories = [...filteredCategories].sort((a, b) =>
    new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
  );

  const totalPages = Math.ceil(sortedCategories.length / itemsPerPage);
  const displayedCategories = sortedCategories.slice((page - 1) * itemsPerPage, page * itemsPerPage);

  useEffect(() => {
    setPage(1);
  }, [searchTerm]);

  const confirmAdd = async () => {
    try {
      // setLoading(true);
      await axios.post(`${process.env.REACT_APP_API_URL}/categories`, { name: newCategoryName });
      setSuccessMessage('Categoria aggiunta con successo');
      setErrorMessage(null);
      fetchCategories();
      setIsAdding(false);
      setNewCategoryName('');
    } catch (error: any) {
      setErrorMessage(error.response?.data?.error || 'Errore durante l\'aggiunta');
      setSuccessMessage(null);
    } finally {
      // setLoading(false);
    }
  };

  const handleAddCategory = () => {
    confirmAdd(); // Appel direct à la fonction d'ajout sans confirmation
  };

  const handleDelete = (category: Category) => {
    setDeleteConfirm(category);
  };

  const confirmDelete = async () => {
    if (!deleteConfirm) return;
    try {
      // setLoading(true);
      await axios.delete(`${process.env.REACT_APP_API_URL}/categories/${deleteConfirm.id}`);
      setSuccessMessage('Categoria eliminata con successo');
      setError(null);
      fetchCategories();
    } catch (error: any) {
      setErrorMessage(error.response?.data?.error || 'Errore durante l\'eliminazione');
      setSuccessMessage(null);
    } finally {
      setDeleteConfirm(null);
      // setLoading(false);
    }
  };

  const handleEdit = (category: Category) => {
  

    if (category.questions) {
      
      category.questions.forEach((question, qIndex) => {
        

        if (question.answers) {
          question.answers.forEach((answer, aIndex) => {
          });
        }
      });
    }

    onEditCategory?.(category.id);
  };


  if (error) {
    return (
      <div className="text-red-500 text-center p-4">
        {error}
      </div>
    );
  }

  return (
    <>
      <div className="bg-white rounded-xl shadow-lg border border-gray-200/80">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center">
            <h2 className="text-xl font-semibold text-gray-800">Elenco delle categorie</h2>
            <div className="flex-1 mx-4">
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Cerca una categoria..."
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
            <span className="px-3 py-1 bg-indigo-50 text-indigo-600 rounded-full text-sm font-medium">
              {sortedCategories.length} categorie
            </span>
            {!isAdding && (
              <button
                onClick={() => { setIsAdding(true); setNewCategoryName(''); }}
                className="ml-4 px-4 py-2 bg-[rgb(51,112,183)] text-white rounded-lg hover:bg-[rgb(89,96,211)]"
              >
                Aggiungi categoria
              </button>
            )}
          </div>

          {/* Messages for success and error */}
          {successMessage && (
            <div className="mt-2 mb-4 px-4 py-2 bg-green-100 text-green-700 text-sm rounded flex items-center justify-between">
              {successMessage}
              <button onClick={() => setSuccessMessage(null)} className="ml-2">
                <FiX className="w-4 h-4 text-green-700" />
              </button>
            </div>
          )}
          {errorMessage && (
            <div className="mt-2 mb-4 px-4 py-2 bg-red-100 text-red-700 text-sm rounded flex items-center justify-between">
              {errorMessage}
              <button onClick={() => setErrorMessage(null)} className="ml-2">
                <FiX className="w-4 h-4 text-red-700" />
              </button>
            </div>
          )}

          {isAdding && (
            <div className="px-6 py-4">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newCategoryName}
                  onChange={(e) => setNewCategoryName(e.target.value)}
                  placeholder="Nome della nuova categoria"
                  className="flex-1 px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                />
                <button
                  onClick={handleAddCategory}
                  className="px-4 py-2 bg-[rgb(51,112,183)] text-white rounded-lg hover:bg-[rgb(89,96,211)]"
                >
                  Aggiungi
                </button>
                <button
                  onClick={() => setIsAdding(false)}
                  className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600"
                >
                  Annulla
                </button>
              </div>
            </div>
          )}
        </div>
        <div className="p-6">
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="bg-gradient-to-r from-[rgb(149,71,254)] via-[rgb(89,96,211)] to-[rgb(51,112,183)]">
                <tr className="border-b border-[rgb(51,112,183)]">
                  <th className="px-6 py-3 text-left text-sm font-medium text-white uppercase tracking-wider">ID</th>
                  <th className="px-6 py-3 text-left text-sm font-medium text-white uppercase tracking-wider">Nome</th>
                  <th className="px-6 py-3 text-left text-sm font-medium text-white uppercase tracking-wider">Data di creazione</th>
                  <th className="px-6 py-3 text-right text-sm font-medium text-white uppercase tracking-wider">Azioni</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {displayedCategories.map((category, index) => (
                  <tr key={category.id} className="odd:bg-white even:bg-gray-50 hover:bg-gray-100 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {(page - 1) * itemsPerPage + index + 1}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm font-medium text-gray-900">{category.name}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(category.created_at).toLocaleDateString('it-IT', {
                        day: 'numeric',
                        month: 'long',
                        year: 'numeric'
                      })}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => handleEdit(category)}
                          className="p-1 rounded-full hover:bg-indigo-50 text-indigo-600 transition-colors"
                          title="Modifica"
                        >
                          <FiEdit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(category)}
                          className="p-1 rounded-full hover:bg-red-50 text-red-600 transition-colors"
                          title="Elimina"
                        >
                          <FiTrash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {sortedCategories.length === 0 && (
                  <tr>
                    <td colSpan={4} className="px-6 py-8 text-center">
                      <div className="flex flex-col items-center gap-2">
                        <p className="text-gray-500 text-sm">Nessuna categoria trovata</p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
        <div className="px-4 py-4 flex justify-between items-center">
        <button
          onClick={() => setPage(page > 1 ? page - 1 : 1)}
          disabled={page === 1}
          className="px-4 py-2 text-sm text-gray-700 bg-gray-200 rounded-lg"
        >
          Precedente
        </button>
        <span className="text-sm text-gray-500">Pagina {page} di {totalPages}</span>
        <button
          onClick={() => setPage(page < totalPages ? page + 1 : totalPages)}
          disabled={page === totalPages}
          className="px-4 py-2 text-sm text-gray-700 bg-gray-200 rounded-lg"
        >
          Successivo
        </button>
      </div>
      </div>
      <ConfirmModal
        isOpen={deleteConfirm !== null}
        onConfirm={confirmDelete}
        onCancel={() => setDeleteConfirm(null)}
        message={`Sei sicuro di voler eliminare la categoria "${deleteConfirm?.name}"?`}
        title="Conferma eliminazione"
      />
    </>
  );
};

export default ListCategories;
